import { inject, Injectable } from '@angular/core';
import { from } from 'rxjs';

import { environment } from '../../../environments/environment';
import { DownloadService } from 'src/app/shared/services/download.service';
import { HttpClient } from '@angular/common/http';
import { IOutputCheck } from 'src/app/spa/models/spa-project.model';
import { DocumentOutcome, IDiscrepancy, IFile, ITlf, TaskOutcome } from 'src/app/discrepancies/models/discrepancies.model';
import { ITask } from 'src/app/task-designer/models/task.model';
import {
  IBulkDocumentApplicabilityData,
  IDocumentsApplicableUpdate,
  IDocumentsOutcomeUpdate
} from '../models/validation.model';
import { IRepeatLogic } from 'src/app/task-designer/models/repeat-logic.model';
import { IGroupCategory } from '../models/project-labels/project-labels';

const SERVER_URL = environment.serverUrl;
const spaEndpoint = SERVER_URL + '/spa/project';
const validationEndpoint = SERVER_URL + '/validation';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {
  private readonly downloadService = inject(DownloadService);
  private readonly http = inject(HttpClient);

  downloadFile(projectID: string, docID: string, extracted: boolean) {
    const url = `${spaEndpoint}/${projectID}/file-export/${docID}`;
    if (!extracted) return from(this.downloadService.download(url));
    return from(this.downloadService.downloadExtracted(url));
  }

  getOutputChecks(projectID: string) {
    return this.http.get<IOutputCheck[]>(`${spaEndpoint}/${projectID}/check-list`);
  }

  updateTlfDetails(
    projectID: string,
    tlfID: string,
    body: Partial<{ title: string[]; footnotes: string[]; number: string; protocol: string }>
  ) {
    return this.http.post<{ tlf: Pick<ITlf, 'id' | 'footnotes' | 'title' | 'protocol' | 'number'> }>(
      `${validationEndpoint}/${projectID}/tlfs/${tlfID}/details`,
      body
    );
  }

  createTask(projectID: string, task: ITask, repeatLogic: IRepeatLogic) {
    return this.http.put<{ discrepancies: IDiscrepancy[]; documentOutcomes: IDocumentsOutcomeUpdate }>(
      `${validationEndpoint}/${projectID}/tasks/create`,
      {
        projectID,
        task,
        repeatLogic
      }
    );
  }

  updateTask(projectID: string, task: ITask) {
    return this.http.patch<{ discrepancy: IDiscrepancy; documentOutcomes: IDocumentsOutcomeUpdate }>(
      `${validationEndpoint}/${projectID}/tasks/${task.id}`,
      {
        projectID,
        task
      }
    );
  }

  updateTaskOutcome(projectID: string, taskID: string, outcome: TaskOutcome) {
    return this.http.patch<{ task: IDiscrepancy; documentOutcomes: IDocumentsOutcomeUpdate }>(
      `${validationEndpoint}/${projectID}/tasks/outcome`,
      {
        taskID,
        outcome
      }
    );
  }

  updateDocumentOutcome(projectID: string, documentID: string, checkKey: string, outcome: DocumentOutcome) {
    return this.http.patch<{ tasks: IDiscrepancy[]; documentOutcomes: IDocumentsOutcomeUpdate }>(
      `${validationEndpoint}/${projectID}/documents/outcome`,
      {
        documentID,
        outcome,
        checkKey
      }
    );
  }

  updateDocumentApplicability(projectID: string, documentID: string, checkKey: string, applicable: boolean) {
    return this.http.patch<{
      documentApplicability: IDocumentsApplicableUpdate;
      tasks?: IDiscrepancy[];
      documentOutcomes?: IDocumentsOutcomeUpdate;
    }>(`${validationEndpoint}/${projectID}/documents/applicability/${documentID}`, {
      documentID,
      applicable,
      checkKey
    });
  }

  getVerifiedTasks(projectID: string, documentID: string) {
    return this.http.get<{ tasks: IDiscrepancy[]; tlfName: string }>(
      `${validationEndpoint}/${projectID}/documents/${documentID}/tasks/verified`
    );
  }

  addAdminRemarks(projectID: string, documentIDs: string[], adminRemark: string) {
    return this.http.patch<{ files: Pick<IFile, 'id' | 'adminRemark'>[] }>(
      `${validationEndpoint}/${projectID}/documents/remark`,
      { documentIDs, adminRemark }
    );
  }

  getAIGroups(projectID: string) {
    return this.http.get<IGroupCategory[]>(`${spaEndpoint}/${projectID}/groups`);
  }

  setBulkDocumentApplicability(projectID: string, data: IBulkDocumentApplicabilityData) {
    return this.http.patch<{
      documentApplicability: IDocumentsApplicableUpdate;
      tasks?: IDiscrepancy[];
      documentOutcomes?: IDocumentsOutcomeUpdate;
    }>(`${validationEndpoint}/${projectID}/documents/applicability/`, { ...data });
  }

  recalculateCounters(projectID: string) {
    return this.http.post<{ success: true }>(`${validationEndpoint}/${projectID}/recalculateCounters`, {});
  }
}
